import * as React from "react";
import { AlertTriangle, Check, History, Info, Lock, X } from "lucide-react";

import { BookingEventView, RollingLinkEvent, PollEvent } from "@/api/types";
import { cn } from "@/lib/utils";

/**
 * The audit trail, drawn one way for every kind of booking link.
 *
 * <p>Ad-hoc links and rolling appointments log different event vocabularies, and each used to have
 * its own renderer, so the same question ("what happened to this link?") looked like two different
 * features. Both vocabularies are normalised to {@link TrailEntry} here and drawn once.</p>
 *
 * <p>This belongs to the read-only side. A rolling link's journal used to sit inside its builder,
 * where an audit log is noise next to the fields you are editing, and where it is easy to mistake
 * history for state you can change.</p>
 */

export type TrailCategory = "booking" | "config";
type Tone = "good" | "warn" | "bad" | "muted";

export interface TrailEntry {
  id: string;
  category: TrailCategory;
  tone: Tone;
  /** What happened, in the host's words. */
  label: string;
  /** Who did it, when that is known and meaningful. */
  who: string | null;
  /** A reason, or a config before/after summary. */
  detail: string | null;
  occurredAt: string;
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** "2d ago" / "3h ago", for when the exact clock time is noise. */
function ago(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return "";
  const min = Math.round(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/**
 * A journal is a historical record, so the three tables below are read defensively.
 *
 * <p>The server owns these vocabularies and can add a value at any time, but a browser is running
 * whatever bundle it last downloaded, and every event ever written stays readable forever. So a
 * value this bundle has never heard of is a normal condition, not a bug, and it has to draw as an
 * ordinary row. It used to throw: the lookup was unguarded, so one `LINK_EXPIRED` in a link's
 * journal took down the whole links page with "Cannot read properties of undefined".</p>
 *
 * <p>The table types stay exhaustive over the API unions, so adding a value to a union still fails
 * the build until it is given a label here. The guard is for drift the compiler cannot see: a
 * backend enum that gained a value the union was never told about, which is exactly how
 * `LINK_EXPIRED` got through.</p>
 */
type AdhocMeta = { tone: Tone; category: TrailCategory; label: string };
type KindMeta = { tone: Tone; label: string };

/** LINK_EXPIRED -> "link expired". Readable enough to ship, until someone writes a real label. */
function humanise(type: string): string {
  return type.toLowerCase().replace(/_/g, " ");
}

function metaOr<M>(table: Record<string, M>, type: string, fallback: M): M {
  return Object.prototype.hasOwnProperty.call(table, type) ? table[type] : fallback;
}

export const ADHOC_META: Record<BookingEventView["type"], AdhocMeta> = {
  BOOKED: { tone: "good", category: "booking", label: "booked" },
  CANCELLED_BY_INVITEE: { tone: "bad", category: "booking", label: "cancelled" },
  CANCELLED_BY_HOST: { tone: "bad", category: "booking", label: "cancelled by host" },
  BOUNCED: { tone: "warn", category: "booking", label: "email bounced, slot released" },
  LINK_CREATED: { tone: "muted", category: "config", label: "link created" },
  LINK_ENABLED: { tone: "muted", category: "config", label: "link enabled" },
  LINK_DISABLED: { tone: "muted", category: "config", label: "link disabled" },
  LINK_EXPIRED: { tone: "muted", category: "config", label: "link expired" },
  LINK_DELETED: { tone: "muted", category: "config", label: "link deleted" },
  ADMIN_DISABLED: { tone: "warn", category: "config", label: "disabled by admin" },
  ADMIN_DELETED: { tone: "warn", category: "config", label: "deleted by admin" },
};

export const ROLLING_META: Record<RollingLinkEvent["type"], KindMeta> = {
  BOOKED: { tone: "good", label: "booked" },
  CANCELLED_BY_INVITEE: { tone: "bad", label: "cancelled" },
  CANCELLED_BY_HOST: { tone: "bad", label: "cancelled by host" },
  RESCHEDULED_BY_INVITEE: { tone: "warn", label: "rescheduled" },
  CLINIC_CREATED: { tone: "muted", label: "link created" },
  CONFIG_CHANGED: { tone: "muted", label: "setting changed" },
  ENABLED: { tone: "muted", label: "link enabled" },
  DISABLED: { tone: "muted", label: "link disabled" },
};

export const POLL_META: Record<PollEvent["type"], KindMeta> = {
  VOTED: { tone: "good", label: "answered" },
  VOTE_AMENDED: { tone: "warn", label: "changed their answer" },
  POLL_CREATED: { tone: "muted", label: "poll created" },
  CONFIG_CHANGED: { tone: "muted", label: "setting changed" },
  OPENED: { tone: "muted", label: "voting reopened" },
  CLOSED: { tone: "muted", label: "voting closed" },
  CONFIRMED: { tone: "good", label: "time confirmed" },
  UNCONFIRMED: { tone: "bad", label: "confirmation withdrawn" },
  CONFIRMATION_SENT: { tone: "good", label: "confirmation sent" },
  DELETED: { tone: "muted", label: "poll removed" },
};

export function bookingEventToEntry(e: BookingEventView): TrailEntry {
  // An unknown value files itself by the same rule the known ones follow: booking-level events
  // carry a booking_id, link-level ones do not. So it still lands under the right filter.
  const meta = metaOr<AdhocMeta>(ADHOC_META, e.type, {
    tone: "muted",
    category: e.booking_id ? "booking" : "config",
    label: humanise(e.type),
  });
  return {
    id: e.id,
    category: meta.category,
    tone: meta.tone,
    label: meta.label,
    // Link-level events are the host's own doing, so naming an actor there adds nothing.
    who: e.booking_id ? (e.actor_email ?? actorName(e.actor)) : null,
    detail: e.reason ?? null,
    occurredAt: e.occurred_at,
  };
}

export function rollingLinkEventToEntry(e: RollingLinkEvent): TrailEntry {
  // The category is the server's own answer, so an unknown type still sorts correctly here.
  const meta = metaOr<KindMeta>(ROLLING_META, e.type, { tone: "muted", label: humanise(e.type) });
  const isBooking = e.category === "BOOKING";
  return {
    id: e.id,
    category: isBooking ? "booking" : "config",
    tone: meta.tone,
    label: meta.label,
    // A booking event's detail is the invitee's name, which reads as the actor, not as a reason.
    who: isBooking ? (e.detail || e.actor_email || actorName(e.actor)) : null,
    detail: isBooking ? null : e.detail,
    occurredAt: e.occurred_at,
  };
}

export function pollEventToEntry(e: PollEvent): TrailEntry {
  const meta = metaOr<KindMeta>(POLL_META, e.type, { tone: "muted", label: humanise(e.type) });
  const isVote = e.category === "VOTE";
  return {
    id: e.id,
    category: isVote ? "booking" : "config",
    tone: meta.tone,
    label: meta.label,
    // A vote event's detail is the respondent's name, which reads as the actor, not as a reason.
    who: isVote ? (e.detail || e.actor_email || actorName(e.actor)) : null,
    detail: isVote ? null : e.detail,
    occurredAt: e.occurred_at,
  };
}

function actorName(actor: string): string {
  return actor === "HOST" ? "You" : actor.toLowerCase();
}

/**
 * What a Free host sees where the trail would be. The audit log is sold from Basic upwards, so the
 * three GET /{id}/events endpoints answer 402 and there is nothing to draw.
 *
 * <p>The second line is the important one: nothing has been lost. Events are recorded at every
 * tier, so upgrading reveals the whole history rather than starting a fresh one, and someone
 * weighing up the upgrade should know that before they decide. Rendered instead of an empty trail,
 * which would otherwise read as "nothing has happened to this link".</p>
 */
export function AuditLogUpsell() {
  return (
    <div className="rounded-xl border border-[hsl(var(--foreground)_/_0.14)] px-4 py-5 text-center">
      <Lock className="mx-auto h-4 w-4 text-muted-foreground" />
      <p className="mt-2 text-sm font-semibold text-foreground">The history of this link is on Basic</p>
      <p className="mx-auto mt-1 max-w-[46ch] text-sm text-muted-foreground">
        Every booking, cancellation and change is already being recorded. Upgrade to read it.
      </p>
      <a
        href="/upgrade"
        className="mt-3 inline-flex items-center rounded-lg bg-brand-blue px-3 py-1.5 text-xs font-bold text-white transition hover:brightness-110"
      >
        See plans
      </a>
    </div>
  );
}

type Filter = "all" | "config" | "booking";

export function EventTrail({ entries, loading }: { entries: TrailEntry[]; loading?: boolean }) {
  const [filter, setFilter] = React.useState<Filter>("all");

  const counts = {
    all: entries.length,
    config: entries.filter((e) => e.category === "config").length,
    booking: entries.filter((e) => e.category === "booking").length,
  };
  const shown = filter === "all" ? entries : entries.filter((e) => e.category === filter);

  const tabs: { key: Filter; label: string }[] = [
    { key: "all", label: `All (${counts.all})` },
    { key: "config", label: `Config (${counts.config})` },
    { key: "booking", label: `Bookings (${counts.booking})` },
  ];

  return (
    <section className="space-y-3 rounded-xl border border-border bg-background/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-foreground">
          <History className="h-3.5 w-3.5 text-muted-foreground" />
          History
        </h3>
        <div className="inline-flex overflow-hidden rounded-lg border border-[hsl(var(--foreground)_/_0.28)] text-xs font-semibold">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              aria-pressed={filter === t.key}
              onClick={() => setFilter(t.key)}
              className={cn(
                "px-3 py-1.5 transition",
                filter === t.key
                  ? "bg-brand-blue text-white"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : shown.length === 0 ? (
        <p className="text-xs italic text-muted-foreground">
          {entries.length === 0 ? "No history yet." : "Nothing in this view."}
        </p>
      ) : (
        <ul className="max-h-72 space-y-1.5 overflow-y-auto">
          {shown.map((e) => (
            <TrailRow key={e.id} entry={e} />
          ))}
        </ul>
      )}
    </section>
  );
}

/** One journal entry: what happened, who did it, and when (its OWN timestamp, not the booking's). */
export function TrailRow({ entry }: { entry: TrailEntry }) {
  const Icon =
    entry.tone === "good" ? Check : entry.tone === "warn" ? AlertTriangle : entry.tone === "bad" ? X : Info;
  const iconColor =
    entry.tone === "good"
      ? "text-green-600"
      : entry.tone === "warn"
        ? "text-amber-600"
        : entry.tone === "bad"
          ? "text-red-600"
          : "text-muted-foreground";
  return (
    <li className="flex items-start gap-2 text-[11px] leading-tight">
      <span className="mt-0.5 shrink-0">
        <Icon className={cn("h-3.5 w-3.5", iconColor)} aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className={cn("font-medium", entry.tone === "bad" && "line-through opacity-70")}>
          {entry.label}
        </span>
        {entry.who && <span className="text-muted-foreground"> · {entry.who}</span>}
        <br />
        <span className="text-muted-foreground">
          {when(entry.occurredAt)} · {ago(entry.occurredAt)}
          {entry.detail ? ` · ${entry.detail}` : ""}
        </span>
      </span>
    </li>
  );
}
